import React, { useState, useEffect, useRef } from 'react';
import { X, Plug, FileSpreadsheet, Loader, CheckCircle, Download, Sparkles, Send } from 'lucide-react';
import { listIntegrations, previewIntegration, importFromIntegration, importCsv, integrationCopilot } from '../services/api';

// Minimal CSV parser (handles quoted fields and commas inside quotes).
function parseCsv(text) {
  const rows = [];
  let row = [], field = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      if (field !== '' || row.length) { row.push(field); rows.push(row); row = []; field = ''; }
    } else field += c;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1).map((r) => Object.fromEntries(headers.map((h, idx) => [h, (r[idx] || '').trim()])));
}

const pick = (o, ...keys) => { for (const k of keys) if (o[k] != null && o[k] !== '') return o[k]; return ''; };
const splitSkills = (s) => (s ? String(s).split(/[,;|]/).map((x) => x.trim()).filter(Boolean) : []);

function csvRowToRequirement(r) {
  return {
    jobTitle: pick(r, 'jobTitle', 'title', 'Job Title', 'Title') || 'Untitled role',
    department: pick(r, 'department', 'Department'),
    jobLocation: pick(r, 'jobLocation', 'location', 'Location'),
    employmentType: pick(r, 'employmentType', 'Employment Type', 'type'),
    salaryRange: pick(r, 'salaryRange', 'Salary Range', 'salary'),
    experienceLevel: pick(r, 'experienceLevel', 'Experience Level', 'level'),
    skills: splitSkills(pick(r, 'skills', 'Skills')),
    external_id: pick(r, 'id', 'external_id', 'ID'),
    external_source: 'csv',
  };
}
function csvRowToCandidate(r) {
  const full = pick(r, 'full_name', 'name', 'Name', 'Full Name');
  return {
    first_name: pick(r, 'first_name', 'First Name') || (full ? full.split(' ')[0] : ''),
    last_name: pick(r, 'last_name', 'Last Name') || (full && full.includes(' ') ? full.split(' ').slice(-1)[0] : ''),
    full_name: full,
    email: pick(r, 'email', 'Email'),
    phone_number: pick(r, 'phone_number', 'phone', 'Phone'),
    skills: splitSkills(pick(r, 'skills', 'Skills')),
    external_id: pick(r, 'id', 'external_id', 'ID'),
    external_source: 'csv',
  };
}

const itemName = (it, entity) => entity === 'candidates'
  ? (it.full_name || `${it.first_name || ''} ${it.last_name || ''}`.trim() || 'Candidate')
  : it.jobTitle;
const itemSub = (it, entity) => entity === 'candidates'
  ? [it.email, (it.skills || []).slice(0, 5).join(', ')].filter(Boolean).join(' · ')
  : [[it.department, it.jobLocation, it.employmentType, it.experienceLevel].filter(Boolean).join(' · '),
     (it.skills || []).slice(0, 5).join(', ')].filter(Boolean).join(' · ');

export default function ImportRequirementsModal({ onClose, onImported, entity = 'requirements' }) {
  const noun = entity === 'candidates' ? 'Candidates' : 'Job Requirements';
  const nounLower = entity === 'candidates' ? 'candidate(s)' : 'requirement(s)';
  const csvHeaders = entity === 'candidates'
    ? 'first_name, last_name, email, phone, skills'
    : 'jobTitle, department, location, employmentType, salaryRange, experienceLevel, skills';

  const [mode, setMode] = useState('copilot'); // copilot | integration | csv
  const [connections, setConnections] = useState([]);
  const [connId, setConnId] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    listIntegrations()
      .then((c) => setConnections((c || []).filter((x) => x.capabilities?.[entity])))
      .catch(() => setConnections([]));
  }, [entity]);

  const resetList = () => { setItems([]); setSelected({}); setResult(null); setError(''); };

  const doPreview = async () => {
    if (!connId) { setError('Choose a connection.'); return; }
    setLoading(true); resetList();
    try {
      const data = await previewIntegration(connId, entity, 50);
      setItems(data.items || []);
      setSelected(Object.fromEntries((data.items || []).map((_, i) => [i, true])));
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reach the source.');
    } finally {
      setLoading(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    resetList();
    const text = await file.text();
    const mapper = entity === 'candidates' ? csvRowToCandidate : csvRowToRequirement;
    const rows = parseCsv(text).map(mapper);
    setItems(rows);
    setSelected(Object.fromEntries(rows.map((_, i) => [i, true])));
  };

  const selectedItems = () => items.filter((_, i) => selected[i]);

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    const next = [...chat, { role: 'user', content: text }];
    setChat(next); setChatInput(''); setChatLoading(true); setError('');
    try {
      const res = await integrationCopilot(next, entity);
      setChat((c) => [...c, { role: 'assistant', content: res.reply }]);
      if (res.items?.length) {
        setItems(res.items);
        setSelected(Object.fromEntries(res.items.map((_, i) => [i, true])));
      }
    } catch (err) {
      setChat((c) => [...c, { role: 'assistant', content: 'Sorry — ' + (err.response?.data?.detail || 'something went wrong.') }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  };

  const doImport = async () => {
    const chosen = selectedItems();
    if (chosen.length === 0) { setError(`Select at least one ${entity === 'candidates' ? 'candidate' : 'role'}.`); return; }
    setImporting(true); setError('');
    try {
      const res = mode === 'integration'
        ? await importFromIntegration(connId, entity, chosen)
        : await importCsv(entity, chosen); // csv + copilot: normalized items
      setResult(res);
      onImported?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const allChecked = items.length > 0 && items.every((_, i) => selected[i]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-ink-900 border border-ink-700 rounded-xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-700">
          <h3 className="text-lg font-semibold text-ink-100">Import {noun}</h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-2 px-5 pt-4">
          {[['copilot', 'Copilot', Sparkles], ['integration', 'From integration', Plug], ['csv', 'From CSV', FileSpreadsheet]].map(([m, label, Icon]) => (
            <button key={m} onClick={() => { setMode(m); resetList(); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${
                mode === m ? 'bg-brand-500 text-white' : 'bg-ink-800 text-ink-300 hover:bg-ink-700'}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          {mode === 'copilot' && (
            <div className="mb-4">
              <div className="border border-ink-700 rounded-lg bg-ink-950/40 p-3 max-h-56 overflow-y-auto space-y-2">
                {chat.length === 0 && (
                  <p className="text-sm text-ink-400">
                    Ask me to pull {entity} from a configured source — I’ll fetch live and propose them below.
                  </p>
                )}
                {chat.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                      m.role === 'user' ? 'bg-brand-500 text-white' : 'bg-ink-800 text-ink-200'}`}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {chatLoading && <p className="text-xs text-ink-500 flex items-center gap-1.5"><Loader className="w-3.5 h-3.5 animate-spin" /> Fetching…</p>}
                <div ref={chatEndRef} />
              </div>
              <div className="flex gap-2 mt-2">
                <input value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendChat(); }}
                  placeholder="Describe what to fetch…"
                  className="flex-1 px-3 py-2 bg-ink-800 border border-ink-700 rounded-lg text-sm text-ink-100" />
                <button onClick={sendChat} disabled={chatLoading || !chatInput.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-400 disabled:opacity-50">
                  <Send className="w-4 h-4" /> Send
                </button>
              </div>
            </div>
          )}
          {mode === 'integration' && (
            <div className="flex gap-2 items-end mb-4">
              <div className="flex-1">
                <label className="block text-xs font-medium text-ink-400 mb-1">Source connection</label>
                <select value={connId} onChange={(e) => setConnId(e.target.value)}
                  className="w-full px-3 py-2 bg-ink-800 border border-ink-700 rounded-lg text-sm text-ink-100">
                  <option value="">Choose a configured source…</option>
                  {connections.map((c) => <option key={c.integration_id} value={c.integration_id}>{c.name}</option>)}
                </select>
              </div>
              <button onClick={doPreview} disabled={loading || !connId}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-400 disabled:opacity-50">
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Browse
              </button>
            </div>
          )}
          {mode === 'csv' && (
            <div className="mb-4">
              <label className="block text-xs font-medium text-ink-400 mb-1">CSV file (headers: {csvHeaders})</label>
              <input type="file" accept=".csv,text/csv" onChange={onFile}
                className="block w-full text-sm text-ink-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-brand-500 file:text-white file:text-sm" />
            </div>
          )}

          {connections.length === 0 && mode === 'integration' && (
            <p className="text-sm text-ink-400">No sources configured for {entity}. An admin can add one under <strong>Integrations</strong>.</p>
          )}

          {error && <div className="text-sm text-red-400 mb-3">{error}</div>}

          {result ? (
            <div className="text-center py-8">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" />
              <p className="text-ink-100 font-medium">Imported {result.imported} {nounLower}.</p>
              {result.skipped > 0 && <p className="text-ink-400 text-sm mt-1">{result.skipped} skipped (already imported).</p>}
              {result.errors?.length > 0 && <p className="text-red-400 text-xs mt-2">{result.errors.length} error(s): {result.errors.slice(0, 3).join('; ')}</p>}
            </div>
          ) : items.length > 0 ? (
            <div className="border border-ink-700 rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 bg-ink-800 text-xs text-ink-300">
                <input type="checkbox" checked={allChecked}
                  onChange={(e) => setSelected(Object.fromEntries(items.map((_, i) => [i, e.target.checked])))} />
                <span>{selectedItems().length} of {items.length} selected</span>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-ink-800">
                {items.map((it, i) => (
                  <label key={i} className="flex items-start gap-3 px-3 py-2 hover:bg-ink-800/60 cursor-pointer">
                    <input type="checkbox" checked={!!selected[i]} className="mt-1"
                      onChange={(e) => setSelected((s) => ({ ...s, [i]: e.target.checked }))} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-100">{itemName(it, entity)}</p>
                      <p className="text-xs text-ink-400">{itemSub(it, entity) || '—'}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {!result && items.length > 0 && (
          <div className="px-5 py-4 border-t border-ink-700 flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-ink-800 text-ink-200 text-sm hover:bg-ink-700">Cancel</button>
            <button onClick={doImport} disabled={importing}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50">
              {importing ? <><Loader className="w-4 h-4 animate-spin" /> Importing…</> : `Import ${selectedItems().length} selected`}
            </button>
          </div>
        )}
        {result && (
          <div className="px-5 py-4 border-t border-ink-700 flex justify-end">
            <button onClick={onClose} className="px-5 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-400">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
